/**
 * API tarifs atelier (`/api/atelier-settings`) — S6 PR5-bis.
 *
 * Routes via `apiClient` (intercepteur 401 + cookie HTTP-only). Typages
 * issus du codegen OpenAPI.
 */

import { apiClient } from './client'
import type {
  AtelierSettingsResponse,
  AtelierSettingsUpdateRequest,
  EtsyDefaultReadinessStateResponse,
  EtsyDefaultReadinessStateUpdateRequest,
  EtsyDefaultReturnPolicyResponse,
  EtsyDefaultReturnPolicyUpdateRequest,
  EtsyDefaultShippingProfileResponse,
  EtsyDefaultShippingProfileUpdateRequest,
  EtsyFreeShippingZonesResponse,
  EtsyFreeShippingZonesUpdateRequest,
  EtsyFreeShippingZonesUpdateResult,
  EtsyReadinessStateDefinitionSummary,
  EtsyReturnPolicySummary,
  EtsyShippingProfileSummary,
  PodDimensionsWhitelistResponse,
  PodDimensionsWhitelistUpdateRequest,
  PodFrameColorsProvisioningSummary,
  PodFrameColorsWhitelistResponse,
  PodFrameColorsWhitelistUpdateRequest,
  PodMatrixDefaultSpecByFinishResponse,
  PodMatrixDefaultSpecByFinishUpdateRequest,
} from '@/types/atelier-settings'

export const atelierSettingsApi = {
  get: async (): Promise<AtelierSettingsResponse> => {
    const { data } = await apiClient.get<AtelierSettingsResponse>('/atelier-settings')
    return data
  },

  update: async (payload: AtelierSettingsUpdateRequest): Promise<AtelierSettingsResponse> => {
    const { data } = await apiClient.patch<AtelierSettingsResponse>('/atelier-settings', payload)
    return data
  },

  // PR-B FE — profile shipping Etsy par défaut (limited/original).
  getEtsyDefaultShippingProfile: async (): Promise<EtsyDefaultShippingProfileResponse> => {
    const { data } = await apiClient.get<EtsyDefaultShippingProfileResponse>(
      '/atelier-settings/etsy-default-shipping-profile',
    )
    return data
  },

  updateEtsyDefaultShippingProfile: async (
    payload: EtsyDefaultShippingProfileUpdateRequest,
  ): Promise<EtsyDefaultShippingProfileResponse> => {
    const { data } = await apiClient.put<EtsyDefaultShippingProfileResponse>(
      '/atelier-settings/etsy-default-shipping-profile',
      payload,
    )
    return data
  },

  // Liste live des shipping profiles Etsy d'une connexion. Lecture
  // synchrone Etsy API — peut lever 503 si OAuth expiré (FE doit
  // afficher fallback input number + bouton "reconnecter Etsy").
  listEtsyShippingProfiles: async (connectionId: number): Promise<EtsyShippingProfileSummary[]> => {
    const { data } = await apiClient.get<EtsyShippingProfileSummary[]>(
      `/platform-connections/${connectionId}/etsy-shipping-profiles`,
    )
    return data
  },

  // 2026-05-15 — readiness state Etsy par défaut (obligatoire physical/both).
  getEtsyDefaultReadinessState: async (): Promise<EtsyDefaultReadinessStateResponse> => {
    const { data } = await apiClient.get<EtsyDefaultReadinessStateResponse>(
      '/atelier-settings/etsy-default-readiness-state',
    )
    return data
  },

  updateEtsyDefaultReadinessState: async (
    payload: EtsyDefaultReadinessStateUpdateRequest,
  ): Promise<EtsyDefaultReadinessStateResponse> => {
    const { data } = await apiClient.put<EtsyDefaultReadinessStateResponse>(
      '/atelier-settings/etsy-default-readiness-state',
      payload,
    )
    return data
  },

  listEtsyReadinessStateDefinitions: async (
    connectionId: number,
  ): Promise<EtsyReadinessStateDefinitionSummary[]> => {
    const { data } = await apiClient.get<EtsyReadinessStateDefinitionSummary[]>(
      `/platform-connections/${connectionId}/etsy-readiness-state-definitions`,
    )
    return data
  },

  // 2026-05-15 — return policy Etsy par défaut (obligatoire physical/both).
  getEtsyDefaultReturnPolicy: async (): Promise<EtsyDefaultReturnPolicyResponse> => {
    const { data } = await apiClient.get<EtsyDefaultReturnPolicyResponse>(
      '/atelier-settings/etsy-default-return-policy',
    )
    return data
  },

  updateEtsyDefaultReturnPolicy: async (
    payload: EtsyDefaultReturnPolicyUpdateRequest,
  ): Promise<EtsyDefaultReturnPolicyResponse> => {
    const { data } = await apiClient.put<EtsyDefaultReturnPolicyResponse>(
      '/atelier-settings/etsy-default-return-policy',
      payload,
    )
    return data
  },

  listEtsyReturnPolicies: async (connectionId: number): Promise<EtsyReturnPolicySummary[]> => {
    const { data } = await apiClient.get<EtsyReturnPolicySummary[]>(
      `/platform-connections/${connectionId}/etsy-return-policies`,
    )
    return data
  },

  // 2026-05-16 — pays free shipping baked (plan iridescent-sprouting-graham).
  getEtsyFreeShippingZones: async (): Promise<EtsyFreeShippingZonesResponse> => {
    const { data } = await apiClient.get<EtsyFreeShippingZonesResponse>(
      '/atelier-settings/etsy-free-shipping-zones',
    )
    return data
  },

  // PUT zones déclenche un backfill Gelato synchrone (5-30s typique,
  // 60s timeout côté BE). Timeout axios à 90s pour absorber le backfill
  // + marge réseau.
  updateEtsyFreeShippingZonesWithBackfill: async (
    payload: EtsyFreeShippingZonesUpdateRequest,
  ): Promise<EtsyFreeShippingZonesUpdateResult> => {
    const { data } = await apiClient.put<EtsyFreeShippingZonesUpdateResult>(
      '/atelier-settings/etsy-free-shipping-zones',
      payload,
      { timeout: 90_000 },
    )
    return data
  },

  // 2026-05-16 — whitelist format_ids POD print (Catalogue POD tab).
  getPodDimensionsWhitelist: async (): Promise<PodDimensionsWhitelistResponse> => {
    const { data } = await apiClient.get<PodDimensionsWhitelistResponse>(
      '/atelier-settings/pod-dimensions-whitelist',
    )
    return data
  },

  updatePodDimensionsWhitelist: async (
    payload: PodDimensionsWhitelistUpdateRequest,
  ): Promise<PodDimensionsWhitelistResponse> => {
    const { data } = await apiClient.put<PodDimensionsWhitelistResponse>(
      '/atelier-settings/pod-dimensions-whitelist',
      payload,
    )
    return data
  },

  // Plan B 2026-05-18 — mapping atelier finish → printable_spec_id.
  getPodMatrixDefaultSpecByFinish: async (): Promise<PodMatrixDefaultSpecByFinishResponse> => {
    const { data } = await apiClient.get<PodMatrixDefaultSpecByFinishResponse>(
      '/atelier-settings/pod-matrix-default-spec-by-finish',
    )
    return data
  },

  updatePodMatrixDefaultSpecByFinish: async (
    payload: PodMatrixDefaultSpecByFinishUpdateRequest,
  ): Promise<PodMatrixDefaultSpecByFinishResponse> => {
    const { data } = await apiClient.put<PodMatrixDefaultSpecByFinishResponse>(
      '/atelier-settings/pod-matrix-default-spec-by-finish',
      payload,
    )
    return data
  },

  // Plan B Palier 3 2026-05-18 — whitelist FrameColor Gelato + provisioning.
  getPodFrameColorsWhitelist: async (): Promise<PodFrameColorsWhitelistResponse> => {
    const { data } = await apiClient.get<PodFrameColorsWhitelistResponse>(
      '/atelier-settings/pod-frame-colors-whitelist',
    )
    return data
  },

  updatePodFrameColorsWhitelist: async (
    payload: PodFrameColorsWhitelistUpdateRequest,
  ): Promise<PodFrameColorsProvisioningSummary> => {
    // Provisioning synchrone côté BE (~5-30s pour 4 colors × 15 dims).
    // Timeout axios à 60s pour absorber la latence + marge.
    const { data } = await apiClient.put<PodFrameColorsProvisioningSummary>(
      '/atelier-settings/pod-frame-colors-whitelist',
      payload,
      { timeout: 60_000 },
    )
    return data
  },
}
